import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ContactType, PatientContact } from '../../../../../generated/graphql';
import InputGroup from '../../../../components/InputGroup';
import CountrySelector from '../../../../components/CountrySelector';
import ErrorMessage from '../../../../components/ErrorMessage';
import Button from '../../../../components/Button';
import InputLabel from '../../../../components/InputLabel';
import {
  fullNameRegisterOptions,
  emailRegisterOptions,
  phoneRegisterOptions,
} from '../../../../lib/register-options';
import { CONTACT_TYPE_OPTIONS, ContactFormData } from './constants';

interface ContactFormProps {
  contact?: PatientContact | null;
  onSubmit: (data: ContactFormData) => Promise<void>;
  isSubmitting?: boolean;
}

export type { ContactFormData };

const getFormDefaultValues = (
  contact?: PatientContact | null,
): ContactFormData => ({
  contactType: contact?.contactType || ContactType.EmergencyContact,
  firstName: contact?.firstName || '',
  lastName: contact?.lastName || '',
  email: contact?.email || '',
  phoneNumber: contact?.phoneNumber || '',
  addressStreet: contact?.addressStreet || '',
  addressCity: contact?.addressCity || '',
  addressState: contact?.addressState || '',
  addressPostalCode: contact?.addressPostalCode || '',
  addressCountry: contact?.addressCountry || '',
  relationshipDescription: contact?.relationshipDescription || '',
});

const ContactForm: React.FC<ContactFormProps> = ({
  contact,
  onSubmit,
  isSubmitting = false,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: getFormDefaultValues(contact),
  });

  // Update form when contact changes (e.g., when switching from add to edit mode)
  useEffect(() => {
    reset(getFormDefaultValues(contact));
  }, [contact, reset]);

  return (
    <div className="rounded-lg bg-white p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Contact Type */}
        <div>
          <InputLabel label="Contact Type" useNaturalLettering>
            <span className="text-red-100">*</span>
          </InputLabel>
          <select
            {...register('contactType', {
              required: 'Contact type is required',
            })}
            className="w-full rounded border-2 border-transparent bg-neutral-50 py-3 px-4 text-body text-neutral-150 transition-colors focus:border-secondary-50 focus:outline-none focus:ring-0"
          >
            {CONTACT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.contactType && (
            <ErrorMessage>{errors.contactType.message}</ErrorMessage>
          )}
        </div>

        {/* Name Fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputGroup
            label="First Name"
            required
            useNaturalLettering
            {...register('firstName', fullNameRegisterOptions)}
            errorMessage={errors.firstName?.message}
          />
          <InputGroup
            label="Last Name"
            required
            useNaturalLettering
            {...register('lastName', fullNameRegisterOptions)}
            errorMessage={errors.lastName?.message}
          />
        </div>

        {/* Email and Phone */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputGroup
            label="Email"
            required
            useNaturalLettering
            {...register('email', emailRegisterOptions)}
            errorMessage={errors.email?.message}
          />
          <InputGroup
            label="Phone Number"
            type="tel"
            required
            placeholder="+1 555 010 1234"
            useNaturalLettering
            {...register('phoneNumber', {
              ...phoneRegisterOptions,
              required: 'Phone number is required',
            })}
            errorMessage={errors.phoneNumber?.message}
          />
        </div>

        {/* Relationship Description */}
        <InputGroup
          label="Relationship Description"
          placeholder="e.g., Mother, Case Manager, Spouse"
          useNaturalLettering
          {...register('relationshipDescription')}
          errorMessage={errors.relationshipDescription?.message}
        />

        {/* Address Section */}
        <div className="space-y-3">
          <InputGroup
            label="Street Address"
            placeholder="123 Main St"
            useNaturalLettering
            {...register('addressStreet')}
            errorMessage={errors.addressStreet?.message}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <InputGroup
              label="City"
              placeholder="City"
              useNaturalLettering
              {...register('addressCity')}
              errorMessage={errors.addressCity?.message}
            />
            <InputGroup
              label="State/Province"
              placeholder="State"
              useNaturalLettering
              {...register('addressState')}
              errorMessage={errors.addressState?.message}
            />
            <InputGroup
              label="Postal Code"
              placeholder="12345"
              useNaturalLettering
              {...register('addressPostalCode')}
              errorMessage={errors.addressPostalCode?.message}
            />
          </div>

          <CountrySelector
            label="Country"
            value={watch('addressCountry') || ''}
            onChange={(value) => setValue('addressCountry', value)}
            errorMessage={errors.addressCountry?.message}
            useNaturalLettering
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="submit"
            title={
              isSubmitting
                ? 'Saving...'
                : contact
                ? 'Update Contact'
                : 'Add Contact'
            }
            theme="primary"
            size="small"
            disabled={isSubmitting}
          />
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
